import * as React from 'react';
import c from 'classnames';
import Immutable from 'immutable';

import { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';
import { Block, Profile, View } from '@uncinc/react-drupal-core';
import { useTranslation } from 'react-i18next';
import {
  Button,
  SynonymList,
  BodyField,
  DynamicMediaItem,
  RelatedContentByTheme,
  HighlightedProfiles,
  ParagraphsRenderer,
  Breadcrumbs,
} from '../../../../../components';
import { TaxonomyTheme } from '../../../../../types/uncinc_profile/taxonomy';
import { SCREEN_WIDE } from '../../../../../config/constants';

interface ThemeProps {
  entity: ImmutableEntity<TaxonomyTheme>;
}

const TaxonomyThemeChild: React.FC<ThemeProps> = ({ entity }) => {
  const { width } = useWindowDimensions();
  const { t } = useTranslation('translation', { keyPrefix: 'pages.taxonomy_theme_page' });

  const theme = entity.toJS();
  const tid = theme.tid[0].value;
  const name = theme.name[0].value;
  const description = theme.description[0]?.processed || '';
  const synonyms = theme.field_synonym;
  const linkUrl = theme.field_link[0]?.alias || theme.field_link[0]?.uri;
  const linkTitle = theme.field_link[0]?.title;
  const media = theme.field_media_item[0];
  const contactPersons = theme.field_contact_person;
  const contactFormBlockEntity = theme.field_contact_form_block[0]?.entity;
  const paragraphs = theme.field_paragraphs;
  const hasMapParagraphs = paragraphs.some((paragraph) => paragraph.entity.type[0].target_id === 'map');
  const classes = c('TaxonomyTheme');

  return (
    <div className={classes}>
      <Container>
        <div className="row">
          <div className="col-12">
            <Breadcrumbs />
          </div>
        </div>

        <div className="content-section mt-0 mb-lg-0">
          <div className="row">
            <div className="col-12 col-lg-7">
              <div className="content-section mt-0">
                <h1 className="u-word-break">{name}</h1>
                {synonyms.length > 0 && (
                  <SynonymList synonyms={synonyms} />
                )}
                {description && (
                  <BodyField
                    html={description}
                  />
                )}
                {linkUrl && (
                  <Button href={linkUrl} className="mt-5">
                    {linkTitle}
                  </Button>
                )}
              </div>

              {width < SCREEN_WIDE && (
                <div className="content-section">
                  <DynamicMediaItem media={media} imageAspectRatio="2_1" aboveTheFold />
                </div>
              )}

              <ParagraphsRenderer
                className="content-section mt-0"
                paragraphs={paragraphs}
                exclude={['map']}
              />
            </div>

            <div className="col-lg-5 col-xl-4 offset-xl-1">
              {width >= SCREEN_WIDE && (
                <div className="sidebar-section">
                  <DynamicMediaItem media={media} imageAspectRatio="2_1" />
                </div>
              )}

              <div className="sidebar-section">
                {contactPersons.length > 0 && (
                  <HighlightedProfiles heading={t('translation:components.default_profile_teaser.title')}>
                    {contactPersons.map((contactPerson) => (
                      <Profile
                        id={contactPerson.target_id}
                      />
                    ))}
                  </HighlightedProfiles>
                )}
              </div>
              <View
                key={`docs_by_theme:${theme.tid[0].value}`}
                id="docs"
                display="docs_by_theme"
                args={[tid, 'document']}
                resetStateOnUnmount
              />
            </div>
          </div>
        </div>
      </Container>

      {hasMapParagraphs && (
        <ParagraphsRenderer
          className="content-section mt-0"
          paragraphs={paragraphs}
          only={['map']}
          fluid
        />
      )}

      <Container>
        {contactFormBlockEntity && (
          <Block entity={Immutable.fromJS(contactFormBlockEntity)} />
        )}
      </Container>

      <RelatedContentByTheme
        themeIds={[tid]}
        themeName={name}
        title={t('related_content_title', { theme: name.toLowerCase() })}
      />
    </div>
  );
};

export default TaxonomyThemeChild;
